import React, { useEffect } from "react";
import { connect } from "react-redux";
import * as shopActions from "../actions/shop";
import { bindActionCreators } from "redux";
import moment from "moment";
import { useTranslation } from "react-i18next";

import Preloader from "../components/Preloader/Preloader";
import Order from "../components/Order/Order";
import formatPrice from "../helpers/formatPrice";
import download from "../images/download.png";
import DeliveryDate from "../components/DeliveryDate/DeliveryDate";

import "./scss/Balance.scss";

const Balance = (props) => {
  const { t } = useTranslation();
  document.title = `Gudema - ${t("balancePage.title")}`;

  const { closeOrder, getBalance, changeReady } = props;

  useEffect(() => {
    closeOrder();
    getBalance();

    return () => {
      changeReady(false);
    };
  }, [closeOrder, getBalance, changeReady]);

  const returnFalse = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const downloadBalance = () => {
    const link = document.createElement("a");
    link.href = "/api/balance.pdf";
    link.setAttribute("download", "balance.pdf");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getPaymentStatusText = (status) => {
  switch (String(status).trim().toUpperCase()) {
    case "PAID":
    case "PAIDED":
    case "PAID_FULL":
    case "APMOKETA":
    case "TRUE":
      return t("paymentStatusText.paid");

    case "NOT_PAID":
    case "UNPAID":
    case "FALSE":
      return t("paymentStatusText.unpaid");

    case "UNDERPAID":
    case "PARTLY_PAID":
    case "PARTIAL":
      return t("paymentStatusText.partial");

    case "OVERPAID":
      return t("paymentStatus.OVERPAID");

    case "WAITING":
    case "PENDING":
      return t("paymentStatusText.waiting");

    default:
      return status || "—";
  }
};

  const getPaymentStatusClass = (status) => {
    switch (String(status)) {
      case "PAID":
      case "PAID_FULL":
      case "APMOKETA":
      case "true":
        return "balance-badge--paid";

      case "NOT_PAID":
      case "UNPAID":
      case "false":
        return "balance-badge--unpaid";

      case "PARTLY_PAID":
      case "PARTIAL":
        return "balance-badge--partial";

      case "WAITING":
      case "PENDING":
        return "balance-badge--waiting";

      default:
        return "balance-badge--paid";
    }
  };

  if (props.order) {
    return (
      <main className="balance-page">
        <div className="balance-page__container">
          <Order />
        </div>
      </main>
    );
  }

  const balances = props.balances || {};
  const orders = balances.orders || [];

  return (
    <main className="balance-page">
      <div className="balance-page__container">
        <div className="balance-page__head">
          <h1>{t("balancePage.title")}</h1>

          <DeliveryDate className="balance-page__delivery-date" />
        </div>

        {!props.isReady && !orders.length ? (
          <Preloader />
        ) : orders.length ? (
          <>
            <div className="balance-card">
              <div className="balance-table">
                <div className="balance-table__head">
                  <div>{t("balancePage.nr")}</div>
                  <div>{t("balancePage.invoiceCreated")}</div>
                  <div>{t("balancePage.paymentDate")}</div>
                  <div>{t("balancePage.paymentStatus")}</div>
                  <div>{t("balancePage.sum")}</div>
                  <div>{t("balancePage.paidSum")}</div>
                  <div>{t("balancePage.remaining")}</div>
                </div>

                
                  {orders.map((item, i) => (
                    <div
                      className="balance-table__row"
                      key={i}
                      onClick={props.getOrder.bind(
                        this,
                        item.id,
                        item.invoiceLink
                      )}
                    >
                      <div data-label={t("balancePage.nr")}>
                        {!item.invoiceLink ? (
                          item.invoiceNo || ""
                        ) : (
                          <a href={item.invoiceLink} onClick={returnFalse}>
                            {item.invoiceNo || ""}
                          </a>
                        )}
                      </div>

                      <div data-label={t("balancePage.invoiceCreated")}>
                        {item.invoiceDate
                          ? moment(item.invoiceDate).format("YYYY-MM-DD")
                          : ""}
                      </div>

                      <div data-label={t("balancePage.paymentDate")}>
                        {item.invoicePaymentDate
                          ? moment(item.invoicePaymentDate).format("YYYY-MM-DD")
                          : ""}
                      </div>

                      <div data-label={t("balancePage.paymentStatus")}>
                        <span
                          className={`balance-badge ${getPaymentStatusClass(
                            item.paidStatus
                          )}`}
                        >
                          {getPaymentStatusText(item.paidStatus)}
                        </span>
                      </div>

                      <div data-label={t("balancePage.sum")}>
                        {item.totalSum ? formatPrice(item.totalSum) : ""}
                      </div>

                      <div data-label={t("balancePage.paidSum")}>
                        {item.paidedAmount ? formatPrice(item.paidedAmount) : ""}
                      </div>

                      <div data-label={t("balancePage.remaining")}>
                        {item.balance ? formatPrice(item.balance) : ""}
                      </div>
                    </div>
                  ))}


                {(balances.totalSum ||
                  balances.totalPaidSum ||
                  balances.balance) && (
                  <div className="balance-table__row balance-table__row--total">
                    <div></div>
                    <div></div>
                    <div></div>
                    <div>{t("balancePage.total")}</div>
                    <div>
                      {balances.totalSum ? formatPrice(balances.totalSum) : ""}
                    </div>
                    <div>
                      {balances.totalPaidSum
                        ? formatPrice(balances.totalPaidSum)
                        : ""}
                    </div>
                    <div>
                      {balances.balance ? formatPrice(balances.balance) : ""}
                    </div>
                  </div>
                )}
              </div>

              <button
                type="button"
                className="balance-card__download"
                onClick={downloadBalance}
              >
                <img src={download} alt="download" />
                {t("balancePage.downloadPdf")}
              </button>
            </div>
          </>
        ) : (
          <div className="balance-page__empty">{t("balancePage.empty")}</div>
        )}
      </div>
    </main>
  );
};

const mapStateToProps = (state) => ({
  balances: state.shop.balances,
  isReady: state.shop.isReady,
  order: state.shop.order,
});

const mapDispatchToProps = (dispatch) => ({
  ...bindActionCreators(shopActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(Balance);
